import { PAYMENT_SETTLED_OPTIONS } from '@ofmos/shared/dashboard';
import type { HealthConfig } from '@ofmos/shared/health-config';
import type { HealthHistoryDay } from '@ofmos/shared/health-history';
import { formatRelative } from '../../../fields/format';
import { formatMoney, type Money } from '../clients/clientModel';
import { ALL_OWNERS, isPaused as isPausedUntil, isWaitingForReply } from '../shared';

/**
 * BEN'S DASHBOARD, AS PURE FUNCTIONS OVER ROWS THE PAGE ALREADY LOADED (redesign register P1,
 * H1, H2, D2, D7, D8, D9, D10, A12, A13; brief `REDESIGN-BRIEF-FE-DASHBOARD-2026-10-04.md`).
 *
 * Everything that decides WHAT the page shows lives here, so it can be tested without a
 * browser: which red client is waiting, gone quiet or needs attention for another reason, in
 * what order, how many each tile counts, what the action line says, which payments are
 * overdue, promised or due soon, which tasks are a client's and which are the other tasks,
 * who is on the onboarding board and which three are the most late. The hooks next door turn
 * records into these shapes; the components only draw them.
 *
 * WHY THE JOIN IS IN THE BROWSER. The owner filter is `enrollment.agency.csm`, two hops, and a
 * condition that crosses a table is SQL-only and refused by the fixture client
 * (`relation-filters.md` D14). The whole client base is a few hundred rows, so the page reads
 * the lists once and joins them here, which also lets every count on the screen come from the
 * same rows.
 */

export type HealthColour = 'Red' | 'Yellow' | 'Green';

export interface AgencyFacts {
  readonly id: string;
  readonly name: string;
  readonly csmId: string | null;
  readonly csmName: string | null;
  readonly lastClientMessageAt: string | null;
  /** The first client message after the newest coach message (touch feed), or null. */
  readonly unansweredSince: string | null;
  readonly unansweredExcerpt: string | null;
  /** "Mark as handled" stamps it (register D2). */
  readonly replyHandledAt: string | null;
  /** `YYYY-MM-DD`; the client is away until this day (register D6, A13). */
  readonly pausedUntil: string | null;
  /** The lifecycle option's LABEL, or null. */
  readonly lifecycle: string | null;
  /** Set when the client churned; with a Churned lifecycle it takes the client off the lists. */
  readonly churnedAt: string | null;
}

export interface StepFacts {
  /** The step's api name (`introduced_at`), the key of the actor map. */
  readonly name: string;
  /** The field's own label, read from metadata. */
  readonly label: string;
  /** Done, or waived because the call already happened (counted as finished, R2-C). */
  readonly done: boolean;
  readonly late: boolean;
  /** Calendar days past the deadline while late, else null. */
  readonly daysLate: number | null;
}

export interface EnrollmentFacts {
  readonly id: string;
  readonly agencyId: string | null;
  readonly agencyLabel: string | null;
  /** Option labels. */
  readonly status: string | null;
  readonly phase: string | null;
  readonly tier: string | null;
  readonly health: HealthColour | null;
  readonly healthReason: string | null;
  readonly healthSince: string | null;
  readonly contractStartDate: string | null;
  /** One entry per onboarding step present in this installation, in the seeded order. */
  readonly steps: readonly StepFacts[];
}

// --- people -------------------------------------------------------------------------------

/** The owner menu's "All clients" (FE-FOUNDATION's owner menu, `app/fulfillment/shared`). */
export { ALL_OWNERS };

/** A person's first word: this page names a person by first name only. */
export function firstName(name: string | null | undefined): string | null {
  if (name === null || name === undefined) return null;
  const first = name.trim().split(/\s+/)[0];
  return first === undefined || first === '' ? null : first;
}

/** The existing rule (`model.ts:107-110` before the redesign): a record with no agency is All's. */
export function ownerMatches(agency: AgencyFacts | undefined, owner: string): boolean {
  if (owner === ALL_OWNERS) return true;
  return agency?.csmId === owner;
}

// --- days ---------------------------------------------------------------------------------

/**
 * CALENDAR DAYS from `from` to `to`, both `YYYY-MM-DD`, through UTC dates so a daylight-saving
 * change never turns two days into one (`shared/checklist.ts` states the same rule).
 */
export function daysBetween(from: string, to: string): number {
  const toDate = (day: string) => {
    const [year = 0, month = 1, date = 1] = day.slice(0, 10).split('-').map(Number);
    return Date.UTC(year, month - 1, date);
  };
  return Math.round((toDate(to) - toDate(from)) / 86_400_000);
}

/** `YYYY-MM-DD` of a local date. */
export function dayKeyOf(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${String(date.getFullYear())}-${month}-${day}`;
}

/** `today` shifted by whole days. */
export function shiftDay(today: string, days: number): string {
  const [year = 0, month = 1, date = 1] = today.split('-').map(Number);
  return dayKeyOf(new Date(year, month - 1, date + days));
}

/**
 * Whole calendar days from an instant's own local day to `today`, or null for no instant.
 * A DATE (`YYYY-MM-DD`) is read as that day.
 */
export function daysSince(instant: string | null, today: string): number | null {
  if (instant === null) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(instant)) return daysBetween(instant, today);
  const parsed = new Date(instant);
  if (Number.isNaN(parsed.getTime())) return null;
  return daysBetween(dayKeyOf(parsed), today);
}

/** "80 days", "1 day": the days column of a client row (`dashboard-v2-side.html:197`). */
export function dayWords(days: number): string {
  return `${String(days)} ${days === 1 ? 'day' : 'days'}`;
}

/** "Saturday, 3 October": the subtitle under the large title (`dashboard-v2-side.html:142`). */
export function daySubtitle(now: Date = new Date()): string {
  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(now);
  const date = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' }).format(now);
  return `${weekday}, ${date}`;
}

// --- waiting and paused -------------------------------------------------------------------

/**
 * THE WAITING RULE (register D2; foundation brief section 12): an agency is waiting when
 * `unanswered_since` is set and `reply_handled_at` is empty or earlier than it. A new client
 * message moves `unanswered_since` past the stamp and brings the client back by itself.
 */
export function isWaiting(
  agency: Pick<AgencyFacts, 'unansweredSince' | 'replyHandledAt'>,
): boolean {
  return isWaitingForReply(agency.unansweredSince, agency.replyHandledAt);
}

/**
 * THE PAUSED RULE (register D6, A13; foundation brief section 13): paused while
 * `paused_until` is today or later. The engine clears the colour within about a minute; the
 * page hides the client at once.
 */
export function isPaused(
  agency: Pick<AgencyFacts, 'pausedUntil'> | undefined,
  today: string,
): boolean {
  return isPausedUntil(agency?.pausedUntil, today);
}

// --- which red list -----------------------------------------------------------------------

/**
 * THE TWO RULES THE RED VIEW NAMES, AS PATTERNS OVER THE STORED SENTENCE. The engine stores
 * only the joined reason sentences, not which rule fired (data report, "Short labels"), so a
 * reason is matched against the TEMPLATE of the rule that wrote it: the `datetime_age` rule
 * reading `agency.last_client_message_at` is the silence rule and the one reading
 * `agency.unanswered_since` is the unanswered rule, found by field id in the live health
 * config, never by a rule name somebody can edit.
 */
export interface ReasonRules {
  readonly silence: RegExp | null;
  readonly unanswered: RegExp | null;
}

export const NO_REASON_RULES: ReasonRules = { silence: null, unanswered: null };

/** A reason template as an anchored pattern: each `{{placeholder}}` matches any words. */
export function reasonPattern(template: string): RegExp {
  const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const literal = (part: string) =>
    part
      .trim()
      .split(/\s+/)
      .filter((word) => word !== '')
      .map(escapeRegExp)
      .join('\\s+');
  const body = template
    .trim()
    .split(/\{\{\s*\w+\s*\}\}/)
    .map(literal)
    .join('\\s*(.*?)\\s*');
  return new RegExp(`^\\s*${body}\\s*$`);
}

export function reasonRulesOf(
  config: HealthConfig | undefined,
  fieldIds: {
    readonly lastClientMessageAt?: string | undefined;
    readonly unansweredSince?: string | undefined;
  },
): ReasonRules {
  if (config === undefined) return NO_REASON_RULES;
  const templateFor = (fieldId: string | undefined) => {
    if (fieldId === undefined) return null;
    const rule = config.rules.find(
      (entry) => entry.kind === 'datetime_age' && entry.fieldPath.fieldId === fieldId,
    );
    return rule === undefined ? null : reasonPattern(rule.reason);
  };
  return {
    silence: templateFor(fieldIds.lastClientMessageAt),
    unanswered: templateFor(fieldIds.unansweredSince),
  };
}

/** `health_reason` holds one sentence per finding, worst first; a row reads the first. */
export function firstReason(reason: string | null): string | null {
  if (reason === null) return null;
  const first = reason.split('\n')[0]?.trim();
  return first === undefined || first === '' ? null : first;
}

/**
 * WHERE A RED CLIENT GOES (coordinator decision Q-DB-1, register D2):
 * - an open message (the waiting rule) puts it in Waiting for our reply;
 * - red BECAUSE of the unanswered rule but no longer waiting means somebody marked it handled
 *   (or the feed already cleared the message) and the colour is a minute or a feed run old:
 *   the page hides it at once, and it does not jump to Gone quiet (D2);
 * - red because of the silence rule is Gone quiet;
 * - any other reason (a coach's assessment, the AI flag, a late step) is the third subhead.
 */
export type RedList = 'waiting' | 'goneQuiet' | 'other' | 'hidden';

export function redListOf(
  agency: AgencyFacts | undefined,
  reason: string | null,
  rules: ReasonRules,
): RedList {
  if (agency !== undefined && isWaiting(agency)) return 'waiting';
  const first = firstReason(reason);
  if (first !== null && rules.unanswered?.test(first) === true) return 'hidden';
  if (first !== null && rules.silence?.test(first) === true) return 'goneQuiet';
  return 'other';
}

// --- clients to focus on ------------------------------------------------------------------

/** Not withdrawn and not completed: the enrollment a client is in right now. */
const ENDED_STATUSES: ReadonlySet<string> = new Set(['Withdrawn', 'Completed']);

export function isActiveEnrollment(enrollment: EnrollmentFacts): boolean {
  return enrollment.status === null || !ENDED_STATUSES.has(enrollment.status);
}

export interface FocusRow {
  readonly enrollment: EnrollmentFacts;
  readonly agency: AgencyFacts | undefined;
}

export interface FocusLists {
  /** Red with an open message, longest wait first. */
  readonly waiting: readonly FocusRow[];
  /** Red for silence, longest silence first. */
  readonly goneQuiet: readonly FocusRow[];
  /** Red for another reason, oldest colour first. */
  readonly other: readonly FocusRow[];
  /** Yellow, oldest colour first (the order the list always had). */
  readonly yellow: readonly FocusRow[];
}

export function nameOf(row: FocusRow): string {
  return row.agency?.name ?? row.enrollment.agencyLabel ?? 'Untitled';
}

const HEALTH_RANK: Readonly<Record<HealthColour, number>> = { Red: 0, Yellow: 1, Green: 2 };

/** Red before Yellow before Green, then the oldest `health_since` first. */
export function compareHealth(a: EnrollmentFacts, b: EnrollmentFacts): number {
  const colour = HEALTH_RANK[a.health ?? 'Green'] - HEALTH_RANK[b.health ?? 'Green'];
  if (colour !== 0) return colour;
  const since = (a.healthSince ?? '').localeCompare(b.healthSince ?? '');
  if (since !== 0) return since;
  return (a.agencyLabel ?? '').localeCompare(b.agencyLabel ?? '');
}

/** Oldest first; an empty instant sorts FIRST (a client who never wrote is the longest silence). */
function compareInstant(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return -1;
  if (b === null) return 1;
  return Date.parse(a) - Date.parse(b);
}

/**
 * TODAY'S ACTIVE CLIENTS, BY THE CLIENTS PAGE'S OWN RULE (`useClients.ts`), which the colour
 * history counts too (`activeClientsOf`, record 11), so the tiles, their deltas, their lines
 * and the lists below them are taken over ONE set. Per agency its current enrollment - an
 * active one before an ended one, then the newest contract start - and the agency is a client
 * while it has not churned and that enrollment is active (any enrollment when the installation
 * has no status field). One row per client, coloured by that enrollment.
 */
export function activeClients(
  enrollments: readonly EnrollmentFacts[],
  agencies: ReadonlyMap<string, AgencyFacts>,
  hasStatusField: boolean,
): readonly FocusRow[] {
  const isActive = (status: string | null) => !hasStatusField || status?.toLowerCase() === 'active';
  const current = new Map<string, EnrollmentFacts>();
  for (const enrollment of enrollments) {
    if (enrollment.agencyId === null) continue;
    const held = current.get(enrollment.agencyId);
    const active = isActive(enrollment.status);
    const heldActive = held !== undefined && isActive(held.status);
    const newer =
      held === undefined || (enrollment.contractStartDate ?? '') > (held.contractStartDate ?? '');
    if (held === undefined || (active && !heldActive) || (active === heldActive && newer)) {
      current.set(enrollment.agencyId, enrollment);
    }
  }
  const clients: FocusRow[] = [];
  for (const agency of agencies.values()) {
    const churned = agency.lifecycle?.toLowerCase() === 'churned' || agency.churnedAt !== null;
    const enrollment = current.get(agency.id);
    if (churned || enrollment === undefined || !isActive(enrollment.status)) continue;
    clients.push({ enrollment, agency });
  }
  return clients;
}

/**
 * THE FOUR LISTS (brief section 5): today's red and yellow active clients of the chosen owner
 * (`activeClients`), without paused clients and without the ones a person just handled or
 * paused on this page, the red ones split by `redListOf`.
 */
export function focusLists(
  clients: readonly FocusRow[],
  owner: string,
  rules: ReasonRules,
  today: string,
  /** Agencies a person just marked handled or paused: hidden at once (D2, A13). */
  hidden: ReadonlySet<string> = new Set(),
): FocusLists {
  const rows = clients
    .filter(
      (row) =>
        (row.enrollment.health === 'Red' || row.enrollment.health === 'Yellow') &&
        ownerMatches(row.agency, owner) &&
        !isPaused(row.agency, today) &&
        (row.agency === undefined || !hidden.has(row.agency.id)),
    )
    .toSorted((a, b) => compareHealth(a.enrollment, b.enrollment));

  const waiting: FocusRow[] = [];
  const goneQuiet: FocusRow[] = [];
  const other: FocusRow[] = [];
  const yellow: FocusRow[] = [];
  for (const row of rows) {
    if (row.enrollment.health === 'Yellow') {
      yellow.push(row);
      continue;
    }
    const list = redListOf(row.agency, row.enrollment.healthReason, rules);
    if (list === 'waiting') waiting.push(row);
    else if (list === 'goneQuiet') goneQuiet.push(row);
    else if (list === 'other') other.push(row);
  }
  const byName = (a: FocusRow, b: FocusRow) => nameOf(a).localeCompare(nameOf(b));
  waiting.sort(
    (a, b) =>
      compareInstant(a.agency?.unansweredSince ?? null, b.agency?.unansweredSince ?? null) ||
      byName(a, b),
  );
  goneQuiet.sort(
    (a, b) =>
      compareInstant(
        a.agency?.lastClientMessageAt ?? null,
        b.agency?.lastClientMessageAt ?? null,
      ) || byName(a, b),
  );
  return { waiting, goneQuiet, other, yellow };
}

/**
 * THE NEEDS ATTENTION TILE'S NUMBER: every client the three red lists hold, not only the rows
 * shown before "Show all", so a client a person just handled or paused leaves the tile with its
 * row (brief section 4, D2; coordinator's decision on FE-DB-12). Keep an eye on is the yellow
 * list's length the same way.
 */
export function needsAttentionCount(lists: FocusLists): number {
  return lists.waiting.length + lists.goneQuiet.length + lists.other.length;
}

/**
 * THE LATE STEP A REASON NAMES: the checkpoint rule's sentence starts with the step's own label
 * ("Introduction to community is 26 days late"), so a Keep an eye on row whose first reason
 * names one of the client's late steps shows that step's days late on the right (mockup
 * `:224`); any other reason shows "wrote <relative>" (Q-DB-3). Null when no late step matches.
 */
export function lateStepOfReason(enrollment: EnrollmentFacts): StepFacts | null {
  const first = firstReason(enrollment.healthReason);
  if (first === null) return null;
  return (
    enrollment.steps.find(
      (step) => step.late && step.daysLate !== null && first.startsWith(`${step.label} `),
    ) ?? null
  );
}

/**
 * EVERY ROW OF THE FOCUS COLUMN, the four lists together: what the owner menu counts, since
 * an option's count is the rows it shows on this page (common brief, FE-FOUNDATION section 10).
 */
export function focusRowsOf(lists: FocusLists): readonly FocusRow[] {
  return [...lists.waiting, ...lists.goneQuiet, ...lists.other, ...lists.yellow];
}

/** The rows a Gone quiet or Keep an eye on list shows before "Show all <N>". */
export const FOCUS_VISIBLE_ROWS = 7;

// --- the action line ----------------------------------------------------------------------

export interface ActionLine {
  /** The number drawn semibold. */
  readonly count: number;
  /** The words after it. */
  readonly rest: string;
}

/**
 * "Start with the <N> clients waiting for our reply." (register P1; mockup script `:346-352`),
 * Gone quiet when nobody is waiting, nothing when both are empty (Q-DB-4).
 */
export function actionLine(waiting: number, goneQuiet: number): ActionLine | null {
  if (waiting > 0) {
    return {
      count: waiting,
      rest: waiting === 1 ? 'client waiting for our reply.' : 'clients waiting for our reply.',
    };
  }
  if (goneQuiet > 0) {
    return {
      count: goneQuiet,
      rest: goneQuiet === 1 ? 'client who has gone quiet.' : 'clients who have gone quiet.',
    };
  }
  return null;
}

/** "wrote 3 days ago", "wrote yesterday", from `last_client_message_at` (Q-DB-3). */
export function wroteWords(instant: string | null, now: Date = new Date()): string | null {
  if (instant === null) return null;
  if (Number.isNaN(new Date(instant).getTime())) return null;
  return `wrote ${formatRelative(instant, now)}`;
}

// --- tiles: deltas and sparklines ---------------------------------------------------------

/** The history reaches back one week: `since` is today minus seven (register D10). */
export const HISTORY_DAYS = 7;

export function historySince(today: string): string {
  return shiftDay(today, -HISTORY_DAYS);
}

export type HistoryColour = 'red' | 'yellow';

/** The part of the history read the tiles use: its own today and its days. */
export interface HistoryLike {
  readonly today: string;
  readonly days: readonly HealthHistoryDay[];
}

export interface HistoryTrend {
  /** The history's count today minus its count on `since`. */
  readonly delta: number;
  /** The history's daily counts of the colour, oldest first. */
  readonly series: readonly number[];
}

/**
 * "+N SINCE LAST WEEK" AND THE TILE'S LINE, FROM THE HISTORY ALONE (register D10; coordinator's
 * decision on FE-DB-12). Both ends of the delta are the history's own counts, its today and
 * `since`, taken over one set of clients by one rule, so nothing changed reads 0. It does not
 * look at the tile's number, which counts the rows on screen (handled, paused and FE-DB-1's
 * hidden clients left out): the two answer different questions and are never subtracted from
 * each other. Null when the history is not there or lacks either day.
 */
export function historyTrend(
  history: HistoryLike | undefined,
  since: string,
  colour: HistoryColour,
): HistoryTrend | null {
  if (history === undefined) return null;
  const now = history.days.find((entry) => entry.day === history.today);
  const then = history.days.find((entry) => entry.day === since);
  if (now === undefined || then === undefined) return null;
  return {
    delta: now[colour] - then[colour],
    series: history.days.map((entry) => entry[colour]),
  };
}

/** "+3", "−2" (a real minus sign), "0": the delta part of the note. */
export function deltaWords(delta: number): string {
  if (delta > 0) return `+${String(delta)}`;
  if (delta < 0) return `−${String(-delta)}`;
  return '0';
}

// --- onboarding ---------------------------------------------------------------------------

/**
 * WHO IS ON THE ONBOARDING BOARD (unchanged rule): phase Pre-onboarding or Activation, OR a
 * contract that started within the last 14 days; never a churned agency or an ended
 * enrollment; and off the board once every step has a date (a waived step counts as done).
 */
const BOARD_PHASES: ReadonlySet<string> = new Set(['Pre-onboarding', 'Activation']);
export const BOARD_RECENT_DAYS = 14;

export function isOnBoard(
  enrollment: EnrollmentFacts,
  agency: AgencyFacts | undefined,
  today: string,
): boolean {
  if (!isActiveEnrollment(enrollment)) return false;
  if (agency?.lifecycle === 'Churned') return false;
  if (enrollment.steps.length > 0 && enrollment.steps.every((step) => step.done)) return false;
  if (enrollment.phase !== null && BOARD_PHASES.has(enrollment.phase)) return true;
  const start = enrollment.contractStartDate;
  if (start === null) return false;
  const days = daysBetween(start.slice(0, 10), today);
  return days >= 0 && days <= BOARD_RECENT_DAYS;
}

/** The step that is most late, or null when none is late. Ties keep the seeded order. */
export function mostLateStep(enrollment: EnrollmentFacts): StepFacts | null {
  let worst: StepFacts | null = null;
  for (const step of enrollment.steps) {
    if (!step.late || step.daysLate === null) continue;
    if (worst === null || step.daysLate > (worst.daysLate ?? 0)) worst = step;
  }
  return worst;
}

export interface OnboardingRow {
  readonly enrollment: EnrollmentFacts;
  readonly agency: AgencyFacts | undefined;
  readonly step: StepFacts;
}

export interface OnboardingSummary {
  /** Board clients for the chosen owner (the In onboarding tile). */
  readonly total: number;
  /** Of them, the ones with a late step. */
  readonly behind: number;
  /** The most late board clients, most late first (the Onboarding block of Today). */
  readonly rows: readonly OnboardingRow[];
}

export const ONBOARDING_ROWS = 3;

export function onboardingSummary(
  enrollments: readonly EnrollmentFacts[],
  agencies: ReadonlyMap<string, AgencyFacts>,
  owner: string,
  today: string,
  limit: number = ONBOARDING_ROWS,
): OnboardingSummary {
  let total = 0;
  let behind = 0;
  const late: OnboardingRow[] = [];
  for (const enrollment of enrollments) {
    const agency = enrollment.agencyId === null ? undefined : agencies.get(enrollment.agencyId);
    if (!isOnBoard(enrollment, agency, today)) continue;
    if (!ownerMatches(agency, owner)) continue;
    total += 1;
    const step = mostLateStep(enrollment);
    if (step === null) continue;
    behind += 1;
    late.push({ enrollment, agency, step });
  }
  late.sort(
    (a, b) =>
      (b.step.daysLate ?? 0) - (a.step.daysLate ?? 0) ||
      (a.agency?.name ?? a.enrollment.agencyLabel ?? '').localeCompare(
        b.agency?.name ?? b.enrollment.agencyLabel ?? '',
      ),
  );
  return { total, behind, rows: late.slice(0, limit) };
}

/** The In onboarding note (Q-DB-5): "All behind", "<N> behind", or nothing. */
export function behindNote(summary: Pick<OnboardingSummary, 'total' | 'behind'>): string | null {
  if (summary.behind === 0 || summary.total === 0) return null;
  if (summary.behind === summary.total) return 'All behind';
  return `${String(summary.behind)} behind`;
}

// --- tasks --------------------------------------------------------------------------------

/** The two lists of Today (brief section 6). A task without `kind` counts as something we owe. */
export type TaskKind = 'open_item' | 'follow_up';

export function taskKindOf(task: object): TaskKind {
  return (task as { readonly kind?: unknown }).kind === 'follow_up' ? 'follow_up' : 'open_item';
}

/** A due date in words: "today", "1 day late", "2 days late", "tomorrow", "in 3 days". */
export function dueWords(dueDate: string | null, today: string): string | null {
  if (dueDate === null) return null;
  const days = daysBetween(today, dueDate.slice(0, 10));
  if (days === 0) return 'today';
  if (days < 0) return `${String(-days)} ${days === -1 ? 'day' : 'days'} late`;
  if (days === 1) return 'tomorrow';
  return `in ${String(days)} days`;
}

/** Overdue first: the oldest due date leads, then the title for a stable order. */
export function compareDue(
  a: { readonly dueDate: string | null; readonly title: string },
  b: { readonly dueDate: string | null; readonly title: string },
): number {
  const due = (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999');
  return due !== 0 ? due : a.title.localeCompare(b.title);
}

export interface TaskLike {
  readonly task: {
    readonly dueDate: string | null;
    readonly title: string;
    readonly doneAt: string | null;
  };
  readonly parent:
    | { readonly access: 'visible'; readonly objectId: string; readonly recordId: string }
    | { readonly access: 'restricted' }
    | { readonly access: 'missing' };
}

export interface TaskLists<T extends TaskLike> {
  /** "We owe the client": open items on a client, most overdue first. */
  readonly owe: readonly T[];
  /** "Follow up with the client": follow-ups on a client, most overdue first. */
  readonly followUp: readonly T[];
  /** Follow-ups past their day, for "<N> late". */
  readonly lateFollowUps: number;
  /** Tasks due today or earlier on a record that is not a client (the H2 line). */
  readonly otherDue: number;
}

/**
 * THE TEAM'S TASKS DUE TODAY OR EARLIER, SPLIT (register H2): a task on an agency is a
 * client's and goes into the two lists when its agency belongs to the chosen owner; a task on
 * any other record leaves the lists and is counted for "<N> other tasks due", under All only,
 * because no agency owns it (the existing owner rule).
 */
export function taskLists<T extends TaskLike>(
  entries: readonly T[],
  agencyObjectId: string | undefined,
  agencies: ReadonlyMap<string, AgencyFacts>,
  owner: string,
  today: string,
): TaskLists<T> {
  const owe: T[] = [];
  const followUp: T[] = [];
  let otherDue = 0;
  for (const entry of entries) {
    const parent = entry.parent;
    const isClient =
      agencyObjectId !== undefined &&
      parent.access === 'visible' &&
      parent.objectId === agencyObjectId;
    if (!isClient) {
      if (owner === ALL_OWNERS) otherDue += 1;
      continue;
    }
    if (!ownerMatches(agencies.get(parent.recordId), owner)) continue;
    if (taskKindOf(entry.task) === 'follow_up') followUp.push(entry);
    else owe.push(entry);
  }
  owe.sort((a, b) => compareDue(a.task, b.task));
  followUp.sort((a, b) => compareDue(a.task, b.task));
  const lateFollowUps = followUp.filter(
    (entry) =>
      entry.task.doneAt === null &&
      entry.task.dueDate !== null &&
      entry.task.dueDate.slice(0, 10) < today,
  ).length;
  return { owe, followUp, lateFollowUps, otherDue };
}

// --- money --------------------------------------------------------------------------------

export interface PaymentFacts {
  readonly id: string;
  /** The deal's agency, the client the row names and opens. */
  readonly agencyId: string | null;
  /** The agency's name, else the deal's label. */
  readonly clientName: string | null;
  readonly amount: Money | null;
  /** `YYYY-MM-DD` or null. */
  readonly dueDate: string | null;
  readonly paidAt: string | null;
  /** The status option's LABEL. */
  readonly status: string | null;
  /** `YYYY-MM-DD`: the client promised to pay by this day (register D6, A12). */
  readonly promisedAt: string | null;
  readonly promiseNote: string | null;
}

const SETTLED_LABELS: ReadonlySet<string> = new Set(
  PAYMENT_SETTLED_OPTIONS.map((option) => option.label.toLowerCase()),
);

/**
 * UNPAID AND UNSETTLED (`shared/dashboard.ts` `outstandingPaymentsStoredFilter`): no paid date,
 * and a status that is empty or not one of the settled options, Paid included (register D8).
 */
export function isOutstanding(payment: PaymentFacts): boolean {
  if (payment.paidAt !== null) return false;
  const status = payment.status?.trim().toLowerCase() ?? '';
  return status === '' || !SETTLED_LABELS.has(status);
}

export function isOverdue(payment: PaymentFacts, today: string): boolean {
  return isOutstanding(payment) && payment.dueDate !== null && payment.dueDate.slice(0, 10) < today;
}

/** A12, variant B: overdue with a promise for today or later steps out of the red list. */
export function isPromised(payment: PaymentFacts, today: string): boolean {
  return (
    isOverdue(payment, today) &&
    payment.promisedAt !== null &&
    payment.promisedAt.slice(0, 10) >= today
  );
}

/** H2: unpaid, unsettled, due from today to today plus 7. */
export function isDueSoon(payment: PaymentFacts, today: string): boolean {
  if (!isOutstanding(payment) || payment.dueDate === null) return false;
  const due = payment.dueDate.slice(0, 10);
  return due >= today && due <= shiftDay(today, 7);
}

export function sumMoney(payments: readonly PaymentFacts[]): Money | null {
  const withAmount = payments.filter((payment) => payment.amount !== null);
  const first = withAmount[0]?.amount;
  if (first === undefined || first === null) return null;
  return {
    currencyCode: first.currencyCode,
    amountMicros: withAmount.reduce(
      (total, payment) => total + (payment.amount?.amountMicros ?? 0),
      0,
    ),
  };
}

export interface MoneyBuckets {
  /** Overdue and not promised, oldest first: the red rows. */
  readonly overdue: readonly PaymentFacts[];
  /** Overdue with a promise for today or later, earliest promise first. */
  readonly promised: readonly PaymentFacts[];
  /** Every overdue payment, promised ones included (the tile, A12's stated downside). */
  readonly overdueCount: number;
  readonly overdueTotal: Money | null;
  /** Days since the oldest overdue due date. */
  readonly oldestDays: number | null;
  /** Due in the next 7 days (H2). */
  readonly dueSoonTotal: Money | null;
}

export function moneyBuckets(payments: readonly PaymentFacts[], today: string): MoneyBuckets {
  const overdueAll = payments
    .filter((payment) => isOverdue(payment, today))
    .sort(
      (a, b) =>
        (a.dueDate ?? '').localeCompare(b.dueDate ?? '') ||
        (a.clientName ?? '').localeCompare(b.clientName ?? ''),
    );
  const promised = overdueAll
    .filter((payment) => isPromised(payment, today))
    .sort((a, b) => (a.promisedAt ?? '').localeCompare(b.promisedAt ?? ''));
  const overdue = overdueAll.filter((payment) => !isPromised(payment, today));
  const oldest = overdueAll[0]?.dueDate ?? null;
  return {
    overdue,
    promised,
    overdueCount: overdueAll.length,
    overdueTotal: sumMoney(overdueAll),
    oldestDays: oldest === null ? null : daysBetween(oldest, today),
    dueSoonTotal: sumMoney(payments.filter((payment) => isDueSoon(payment, today))),
  };
}

/** "8 payments · oldest 74 days" (`dashboard-v2-side.html:172`). */
export function moneyNote(
  buckets: Pick<MoneyBuckets, 'overdueCount' | 'oldestDays'>,
): string | null {
  if (buckets.overdueCount === 0) return null;
  const count = `${String(buckets.overdueCount)} ${buckets.overdueCount === 1 ? 'payment' : 'payments'}`;
  return buckets.oldestDays === null ? count : `${count} · oldest ${dayWords(buckets.oldestDays)}`;
}

// --- the quiet lines (register H2, Q-DB-10, Q-DB-11) -------------------------------------

export function pendingWords(count: number): string {
  return `${String(count)} ${count === 1 ? 'change' : 'changes'} waiting for your approval`;
}

export function otherTasksWords(count: number): string {
  return `${String(count)} other ${count === 1 ? 'task' : 'tasks'} due`;
}

export function dueSoonWords(total: Money): string {
  return `${formatMoney(total)} due in the next 7 days`;
}

export function intakesWords(count: number): string {
  return `${String(count)} ${count === 1 ? 'intake' : 'intakes'} to review`;
}
